import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute} from "@angular/router";

import * as Search from '@app/models/search';
import * as Objects from '@core/objects';
import * as fromState from '@app/store/state/approvals/approvals.state';
import { VersionFeedback } from '@core/models/version-feedback';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionListItemMenuClick } from '@core/components/versions/list/version-list-item-menu-click';
import { NotificationsService } from '@core/services/notifications/notifications.service';


import { AppService } from '@app/services/app/app.service';
import { AppRouterService } from '@app/services/app/router.service';
import { ApprovalsService, ApprovalsSearchService } from '@app/services/app/approvals.service';
import { VersionsService } from '@app/services/app/versions.service';
import { AddProjectService } from '@app/services/app/add-project.service';
import { Session } from '@app/store/models/session.model';

@Component({
  selector: 'approvals-results-main',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalsResultsComponent implements OnInit, OnDestroy, OnChanges {


  public readonly ACCESS_ADD_PROJECT = "access_global_new_request";
  public readonly PLUGIN_REQUEST_FEEDBACK = 'share_feedback';
  public readonly GLOBAL_ACCESS_APPROVALS = "access_global_approvals";

  public readonly SEARCH_STATUS = Search.SearchHttpStatus;

  @Input() params: Search.SearchQueryParams = null;
  @Input() itemsList: Objects.Version[] = null;
  
  public addProjectAccess: boolean = false;
  public feedbacksAccess: boolean = false;
  public globalApprovalsAccess: boolean | string = false; // approvals control
  public term: string = '';
  public loadingMore: boolean = false;
  public refreshing: boolean = false;
  public loading: boolean = true;
  // public loadingFeedbacks: boolean = false;
  public hasSearch: boolean = false;
  public resultsStatus: fromState.ApprovalsResultsStatus = {completed: Search.SearchHttpStatus.INITIAL, uncompleted: Search.SearchHttpStatus.INITIAL}
  public excludedLabels: Objects.Entity[] = [];

  public menu: Array<ThreeDotsMenuItem> = [
      {
          title: "Download All Files",
          key: "download-all",
          icon: "download",
          order: 1
      },
      {
          title: "See Artwork Details",
          key: "details",
          icon: "see-details",
          order: 2
      },
      {
          title: "See Original Project",
          key: "see-project",
          icon: "see-project",
          order: 3
      }
  ];

  // optional menu item
  protected menuAddProject: ThreeDotsMenuItem = {
    title: "Start New Project",
    key: "add-project",
    icon: "add",
    order: 4
  };

  public displayedItems: Objects.Version[] = null;
  
  public feedbacks: fromState.ApprovalsResultsFeedbacks = null;
  public approvalStatusFilter: string = '';

  protected resultsStatusSubscriptor: Subscription;
  protected appliedFiltersSubscription: Subscription;
  protected appSubscription: Subscription;
  protected termSubscription: Subscription;
  protected urlSubscription: Subscription;
  // object subscriptor
  protected selectFeedbacksResultsSubscriptor: Subscription

  constructor(
    private app: AppService,
    private approvalsSrv: ApprovalsService,
    private approvalsSearchSrv: ApprovalsSearchService,
    private appRouter: AppRouterService,
    private notifSrv: NotificationsService,
    private versionsSrv: VersionsService,
    private addProjectSrv: AddProjectService,
    private cdr: ChangeDetectorRef
    ) { 
      // cdr.detach()
    }

  ngOnInit() {
    this.updateItems();

    this.urlSubscription = this.approvalsSrv.selectResultSelectedStatus().subscribe((selectedStatus: string) => {
      if(selectedStatus != this.approvalStatusFilter) {
        this.approvalStatusFilter =  selectedStatus;
      }
    });

    this.resultsStatusSubscriptor = this.approvalsSrv.selectResultStatus().subscribe((rStatus: fromState.ApprovalsResultsStatus) => {
        this.resultsStatus = rStatus;
    });
    // Fix for the HAS search we need to take into account the params allways to show the proper message
    this.appliedFiltersSubscription = this.approvalsSearchSrv.selectFilters().subscribe((filters: Search.SearchAppliedFilter[]) => {
        this.hasSearch = (filters.length >0 || this.params != null)
        this.excludedLabels = filters.filter((item) => item.key == 'label').map((f)=> f.item);
    });

    this.termSubscription = this.approvalsSearchSrv.selectTerm().subscribe((term: string) => {
        this.hasSearch = (term !='' || this.params != null)
    } );

    // get q, global access and plugins access
    this.appSubscription = this.app.getSession().subscribe( (session: Session) => {
      this.feedbacksAccess = session.options.plugins.some((pa: string) => pa == this.PLUGIN_REQUEST_FEEDBACK);
      if(session.options.access.hasOwnProperty(this.GLOBAL_ACCESS_APPROVALS) ){
        this.globalApprovalsAccess = session.options.access[this.GLOBAL_ACCESS_APPROVALS];
      }
      this.addProjectAccess = session.options.access.hasOwnProperty(this.ACCESS_ADD_PROJECT)? session.options.access[this.ACCESS_ADD_PROJECT] : false;
      if(this.addProjectAccess){
          this.addToMenu(this.menuAddProject);
      }
      // session data already laoded.
      this.loading = false;
      this.init();
    });

  }


  ngOnChanges(changes: SimpleChanges): void {
    if(changes.itemsList){
      this.updateItems();
      this.loadingMore = false; // for stop loading more 
    }
  }

  ngOnDestroy() {
    this.appSubscription.unsubscribe();
    this.urlSubscription.unsubscribe();
    this.termSubscription.unsubscribe();
    this.resultsStatusSubscriptor.unsubscribe();
    this.appliedFiltersSubscription.unsubscribe();
    if (this.selectFeedbacksResultsSubscriptor)
        this.selectFeedbacksResultsSubscriptor.unsubscribe();
  }



  // needed to load feedbacks properly anf check current status displayed to manage load
  init() {
    if(!this.loading && !this.loadingMore && this.displayedItems != null && this.displayedItems.length > 0 && this.feedbacksAccess && this.globalApprovalsAccess)
      this.loadResultsFeedbacks();
  }

  updateItems(){
    this.displayedItems = this.itemsList;
    // this.cdr.reattach();
    this.init();
  }
 

  loadMore() {
    this.loadingMore = true;
    if( this.approvalStatusFilter == "completed")
      this.approvalsSrv.loadMoreCompletedResults();
    else { this.approvalsSrv.loadMoreUncompletedResults();}
  }

  onMenuClick(menu: VersionListItemMenuClick) {
    // console.debug(" menu version item  click ",menu);
      switch(menu.key) {
          case "download-all":
              // start notification to download compressed version files in zip
              this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILES_COMPRESSION_START, {id: menu.version.id, details: { url: "/versions/"+menu.version.id }});
              break;
          case "details":
              // go to version Details
              if(menu.version != null)
                  this.appRouter.navigate("/versions/"+menu.version.id);
              break;
          case "see-project":
              // navigate
              if(menu.version != null){
                  this.versionsSrv.getProject$(menu.version).subscribe((originalProject: Objects.Request) => {
                      if(originalProject != null)
                          this.appRouter.navigate('/projects/'+originalProject.id);
                  });
              }
              break;
          case "add-project":
              if(menu.version != null){
                  this.addProjectSrv.clear();
                  this.appRouter.navigate('/projects/add', { queryParams: { version: menu.version.id } });
              }
              break;
      }
  }

  onFileClick(version: Objects.Version,file: Objects.File){
      if (file != null && version != null){
          // set this url to preview version FILE
          this.appRouter.navigate("/versions/"+version.id + "/files/"+file.id);
      }
  }

  onLabelClick(tag: Objects.Label):void{
    if (tag != null ){
        // send this tag to the filters here!!!
        this.approvalsSearchSrv.applyFilter({key:"label",item: tag});
    }
  }

  onScroll(){
      if(this.resultsStatus[this.approvalStatusFilter] ==  Search.SearchHttpStatus.DONE ){
          this.loadMore();
      }
  }

  protected addToMenu(item: ThreeDotsMenuItem) {
    this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
    this.menu.push(item);
  }

  public getFeedbacks(version: Objects.Version) : VersionFeedback[] {
    // when there are no new versions (INITIAL) or feedbacks loading (LOADING) must be null to show dimmed bubbles
    if(this.feedbacks == null || this.feedbacks.status[this.approvalStatusFilter] == (Search.SearchHttpStatus.INITIAL || Search.SearchHttpStatus.LOADING ) )// != done is loading
        return null;
    let vFeedback = this.feedbacks[this.approvalStatusFilter].filter((f: VersionFeedback) => f.version.id == version.id)
    return vFeedback;
  }

  // this function load an object ApprovalsResultsFeedbacks with all inside from the store, so only one suscriptor
  protected loadResultsFeedbacks(){
    this.selectFeedbacksResultsSubscriptor = this.approvalsSrv.selectFeedbacksResults().subscribe((approvalsFeedbacks:fromState.ApprovalsResultsFeedbacks) => {
      this.feedbacks = approvalsFeedbacks;
    });
  }

  public updateVersionStatus(status, version){
    this.approvalsSrv.setVersionStatus(version.id,status)
  }
}
